import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modal";

export const NOTE_MAX = 500;

/**
 * The basket's メモ (T07-41/42): a short note for the staff — 「お取り置き 18時まで」, 「箱なし」 …
 * Saved on the server (`PATCH /pos/carts/{id}`, If-Match) so another staff member signing in at
 * this register sees it; never printed on the receipt, amounts do not change. Empty = removed.
 */
export function NoteModal({
  open,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  current: string | null;
  onClose: () => void;
  onSave: (note: string) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(current ?? "");
  const [busy, setBusy] = useState(false);
  const save = async (value: string) => {
    setBusy(true);
    const ok = await onSave(value);
    setBusy(false);
    if (ok) {
      onClose();
    }
  };
  return (
    <Modal
      open={open}
      title={t("cart.note_title")}
      description={t("cart.note_help")}
      onClose={onClose}
      busy={busy}
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void save(text);
        }}
      >
        <div className="flex flex-col gap-1">
          <label htmlFor="cart-note-text" className="font-medium">
            {t("cart.note_label")}
          </label>
          <textarea
            id="cart-note-text"
            value={text}
            maxLength={NOTE_MAX}
            rows={4}
            onChange={(e) => setText(e.target.value)}
            className="rounded-md border p-3 text-lg"
          />
          <span className="self-end text-sm text-muted-foreground tabular-nums">
            {text.length}/{NOTE_MAX}
          </span>
        </div>
        <div className="flex justify-end gap-2">
          {current ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void save("")}
              className="min-h-pos-touch rounded-md border px-5"
            >
              {t("cart.note_remove")}
            </button>
          ) : null}
          <button
            type="submit"
            disabled={busy || text.trim() === (current ?? "").trim()}
            className="min-h-pos-touch rounded-md bg-primary px-6 font-semibold text-primary-foreground disabled:opacity-50"
          >
            {busy ? t("app.saving") : t("cart.note_save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
